import "./holiday.css";
import { useEffect, useState } from "react";
import { InputNumber, Modal } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spinner from "@src/components/spinner";
import { InfoHint } from "@src/components/infoHint/infoHint";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getHolidayRequestsQuery, previewHolidayRequestQuery } from "@src/util/query/holiday.query";
import { approveHolidayRequestMutation, cancelHolidayRequestMutation, rejectHolidayRequestMutation } from "@src/util/mutation/holiday.mutation";

export const HOLIDAY_TYPES = [
    { value: "AH", label: "Annual Leave", tone: "annual" },
    { value: "BH", label: "Bank Holiday", tone: "bank" },
    { value: "SL", label: "Sick Leave", tone: "sick" },
    { value: "UL", label: "Unpaid Leave", tone: "other" },
    { value: "ML", label: "Maternity Leave", tone: "other" },
    { value: "PL", label: "Paternity Leave", tone: "other" },
    { value: "CL", label: "Compassionate Leave", tone: "other" },
    { value: "TL", label: "Time Off in Lieu (TOIL)", tone: "other" },
    { value: "OL", label: "Other Leave", tone: "other" },
];
/** Request statuses as shown everywhere (stored as pending / approved / rejected / cancelled) */
export const STATUS_LABEL = { pending: "Pending Approval", approved: "Approved", rejected: "Declined", cancelled: "Cancelled" };
/** Where a request came from */
export const SOURCE_LABEL = { mobile: "Mobile App", web: "Web Portal", verify: "WFC Verify" };
export const holidayTypeOf = (code) => HOLIDAY_TYPES.find((type) => type.value === code) || HOLIDAY_TYPES[HOLIDAY_TYPES.length - 1];

const ukDate = (date) => dayjs(date).format("DD/MM/YYYY");
export const requestDates = (request) => {
    const half = (portion) => (portion === "am" ? " (AM)" : portion === "pm" ? " (PM)" : "");
    if (!request?.startDate) return "";
    return request.startDate === request.endDate
        ? `${ukDate(request.startDate)}${half(request.startPortion)}`
        : `${ukDate(request.startDate)}${half(request.startPortion)} to ${ukDate(request.endDate)}${half(request.endPortion)}`;
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const amountText = (days, hours, unit) => (unit === "hours"
    ? `${round2(hours)} hour${round2(hours) === 1 ? "" : "s"}`
    : `${round2(days)} day${round2(days) === 1 ? "" : "s"}`);

/** Finds a request when only its id is known (e.g. from a schedule day row) */
const useRequest = (request, requestId, employeeId, skip) => {
    const { data, loading } = useQuery(getHolidayRequestsQuery, {
        variables: { filter: { employeeId } },
        skip: skip || !!request || !requestId || !employeeId,
        fetchPolicy: "network-only",
    });
    const found = data?.getHolidayRequests?.response?.find((r) => r?._id === requestId);
    return { request: request || found, loading };
};

const ModalShell = ({ visible, close, title, children, footer }) => (
    <Modal open={visible} width={560} footer={footer} closable={false} onCancel={close} zIndex={1100}>
        <div className="modal-header-title">
            <span>{title}</span>
            <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
        </div>
        <div className="modal-body holiday-action-body">{children}</div>
    </Modal>
);

const footerOf = (buttons) => (
    <div className="drawer-footer holiday-action-footer">{buttons}</div>
);

const Summary = ({ request }) => (
    <div className="holiday-action-summary">
        <div className="holiday-action-summary-name">{request?.employeeName}</div>
        <div>{holidayTypeOf(request?.holidayType).label} · {requestDates(request)}</div>
        {request?.holidayComment && <div className="holiday-action-comment">"{request.holidayComment}"</div>}
    </div>
);

/**
 * Approve a request. Shows what it takes off and what's left after (the server's preview
 * working, behind an "i" too), and lets the manager change the amount - e.g. to a full
 * 12-hour shift for a guard whose day off would otherwise count as 1/7 of their week.
 */
export const ApproveHolidayModal = ({ visible, close, onDone, request: givenRequest, requestId, employeeId }) => {
    const dispatch = useDispatch();
    const { request, loading: requestLoading } = useRequest(givenRequest, requestId, employeeId, !visible);
    const [amount, setAmount] = useState(null);
    const [note, setNote] = useState("");
    const [payRate, setPayRate] = useState(null);
    useEffect(() => { if (visible) { setAmount(null); setNote(""); setPayRate(null); } }, [visible, request?._id]);

    const { data, loading: previewLoading } = useQuery(previewHolidayRequestQuery, {
        skip: !visible || !request?._id,
        fetchPolicy: "network-only",
        variables: {
            input: {
                _id: request?._id, employeeId: request?.employeeId, holidayType: request?.holidayType,
                startDate: request?.startDate, endDate: request?.endDate, startPortion: request?.startPortion, endPortion: request?.endPortion,
            },
        },
    });
    const preview = data?.previewHolidayRequest?.response;
    const unit = preview?.unit || "days";
    // no own pay rate and none set yet: ask for the hourly rate now (or leave it for the notice)
    const needsRate = !!preview?.pay && !preview.pay.ownRate && !preview.pay.setRate;
    const suggestion = preview?.pay?.suggestion;
    const [approve, { loading: approving }] = useMutation(approveHolidayRequestMutation);

    const submit = async () => {
        try {
            const changed = amount !== null && amount !== undefined;
            await approve({
                variables: {
                    id: request._id,
                    note: note?.trim() || null,
                    days: changed && unit === "days" ? amount : null,
                    hours: changed && unit === "hours" ? amount : null,
                    ...(needsRate && Number(payRate) > 0 ? {
                        payRate: Number(payRate),
                        payCurrency: suggestion?.currency || null,
                        payRateNote: suggestion && Number(payRate) === suggestion.rate
                            ? `Rate at ${suggestion.siteName}${suggestion.dutyType ? ` (${suggestion.dutyType})` : ""}, last worked ${dayjs(suggestion.date).format("DD/MM/YYYY")}`
                            : "Set by a manager",
                    } : {}),
                },
            });
            dispatch(setNotification({ error: false, notificationState: true, message: "Leave request approved" }));
            close();
            onDone?.();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <ModalShell
            visible={visible}
            close={close}
            title="Approve Leave Request"
            footer={footerOf(<>
                <button className={approving || !request ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={approving || !request} onClick={submit}>
                    {approving ? <Spinner /> : "Approve"}
                </button>
                <button className="drawer-outlined-btn" onClick={close} disabled={approving}>Cancel</button>
            </>)}
        >
            {requestLoading || (!request && visible) ? (
                <div className="holiday-action-loading">{requestLoading ? <Spinner /> : "This request is no longer pending approval."}</div>
            ) : (
                <>
                    <Summary request={request} />
                    <div className="holiday-action-preview">
                        <div className="holiday-action-label">
                            Deduction
                            {preview && <InfoHint title="How this is calculated" lines={preview?.balance?.lines} warnings={preview?.balance?.warnings} />}
                        </div>
                        {previewLoading ? <Spinner /> : (preview?.lines || []).map((line, i) => <div key={i} className="holiday-action-line">{line}</div>)}
                    </div>
                    {preview?.deducts && preview?.balance?.accrues && (
                        <div className="holiday-action-field">
                            <div className="holiday-action-label">
                                Override Deduction (Optional)
                                <InfoHint
                                    title="Overriding the deduction"
                                    lines={[
                                        `Leave empty to use the worked-out ${amountText(preview?.days, preview?.hours, unit)}.`,
                                        unit === "hours"
                                            ? "Type the hours they would really have worked - e.g. 12 for a 12-hour shift. It's spread across the days of the request."
                                            : "Type the days to take off instead. It's spread across the days of the request.",
                                    ]}
                                />
                            </div>
                            <InputNumber
                                min={0}
                                step={unit === "hours" ? 0.5 : 0.25}
                                value={amount}
                                placeholder={String(round2(unit === "hours" ? preview?.hours : preview?.days))}
                                onChange={setAmount}
                                addonAfter={unit}
                                style={{ width: 200 }}
                            />
                        </div>
                    )}
                    {needsRate && (
                        <div className="holiday-action-field">
                            <div className="holiday-action-label">
                                Holiday Pay Rate (Optional)
                                <InfoHint title="Holiday pay rate" lines={[
                                    "They have no pay rate of their own, and site / duty type pay tables don't apply to a holiday, so it needs an hourly rate.",
                                    suggestion ? `Suggested: ${suggestion.text}.` : "There's no completed duty with calculated pay that month (or the 90 days before) to suggest a rate from.",
                                    "Leave it empty to set it later - it will show on the dashboard and on their profile until it's set.",
                                ]} />
                            </div>
                            <InputNumber min={0} step={0.25} value={payRate} onChange={setPayRate}
                                placeholder={suggestion ? String(suggestion.rate) : "Rate"} addonAfter={`${suggestion?.currency || "GBP"} per hour`} style={{ width: 200 }} />
                            <div className="holiday-field-hint">
                                {suggestion ? <>{suggestion.text}. <button className="holiday-link-btn" onClick={() => setPayRate(suggestion.rate)}>Apply {suggestion.rate}</button></> : "Enter the hourly rate, or set it later."}
                            </div>
                        </div>
                    )}
                    {preview?.pay?.ownRate && (
                        <div className="holiday-field-hint" style={{ marginTop: -6 }}>Paid at the employee's own pay rate: {preview.pay.ownRate.rate}{preview.pay.ownRate.currency ? ` ${preview.pay.ownRate.currency}` : ""} per hour.</div>
                    )}
                    <div className="holiday-action-field">
                        <div className="holiday-action-label">Note to Employee (Optional)</div>
                        <TextArea className="generic-input-control" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                </>
            )}
        </ModalShell>
    );
};

/** Reject (pending) or cancel (pending / approved) - same shape, a reason the employee sees */
const DecisionModal = ({ kind, visible, close, onDone, request: givenRequest, requestId, employeeId }) => {
    const dispatch = useDispatch();
    const { request, loading } = useRequest(givenRequest, requestId, employeeId, !visible);
    const [note, setNote] = useState("");
    useEffect(() => { if (visible) setNote(""); }, [visible]);
    const [run, { loading: running }] = useMutation(kind === "reject" ? rejectHolidayRequestMutation : cancelHolidayRequestMutation);

    const submit = async () => {
        try {
            await run({ variables: { id: request._id, note: note?.trim() || null } });
            dispatch(setNotification({ error: false, notificationState: true, message: kind === "reject" ? "Leave request declined" : "Leave cancelled" }));
            close();
            onDone?.();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <ModalShell
            visible={visible}
            close={close}
            title={kind === "reject" ? "Decline Leave Request" : "Cancel Leave"}
            footer={footerOf(<>
                <button className={running || !request ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={running || !request} onClick={submit}>
                    {running ? <Spinner /> : kind === "reject" ? "Decline" : "Cancel Leave"}
                </button>
                <button className="drawer-outlined-btn" onClick={close} disabled={running}>Back</button>
            </>)}
        >
            {loading || !request ? <div className="holiday-action-loading"><Spinner /></div> : (
                <>
                    <Summary request={request} />
                    <div className="holiday-action-line">
                        {kind === "reject"
                            ? "The employee will be notified that the request has been declined. The dates are released on the schedule."
                            : "The days are returned to the employee's balance and released on the schedule. The employee will be notified."}
                    </div>
                    <div className="holiday-action-field">
                        <div className="holiday-action-label">Reason (Optional, Visible to the Employee)</div>
                        <TextArea className="generic-input-control" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                </>
            )}
        </ModalShell>
    );
};

export const RejectHolidayModal = (props) => <DecisionModal kind="reject" {...props} />;
export const CancelHolidayModal = (props) => <DecisionModal kind="cancel" {...props} />;
